
import { useState } from 'react';
import { products, categories } from '../../../mocks/products';
import ProductModal from './ProductModal';
import { useScrollAnimation } from '../../../hooks/useScrollAnimation';

const INITIAL_LIMIT = 8;

export default function Products() {
  const [activeCategory, setActiveCategory] = useState('all');
  const [activeSubcategory, setActiveSubcategory] = useState('all');
  const [selectedProduct, setSelectedProduct] = useState<typeof products[0] | null>(null);
  const [showAll, setShowAll] = useState(false);

  const headerAnim = useScrollAnimation({ threshold: 0.2 });

  const currentCategory = categories.find((c) => c.id === activeCategory);

  const filtered = products.filter((p) => {
    if (activeCategory === 'all') return true;
    if (activeSubcategory === 'all') return p.category === activeCategory;
    return p.category === activeCategory && p.subcategory === activeSubcategory;
  });

  const isAllCategory = activeCategory === 'all';
  const displayedProducts = isAllCategory && !showAll ? filtered.slice(0, INITIAL_LIMIT) : filtered;
  const hasMore = isAllCategory && filtered.length > INITIAL_LIMIT;

  const handleCategoryChange = (id: string) => {
    setActiveCategory(id);
    setActiveSubcategory('all');
    setShowAll(false);
  };

  return (
    <section id="products" className="py-14 lg:py-24 bg-[#0F0F0F]" data-product-shop>
      <div className="max-w-7xl mx-auto px-5 lg:px-16">
        {/* Header */}
        <div
          ref={headerAnim.ref}
          className={`mb-8 lg:mb-14 transition-all duration-700 ${headerAnim.isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}
        >
          <div className="inline-flex items-center gap-2 bg-[#FF6600]/20 border border-[#FF6600]/40 text-[#FF6600] px-3 py-1.5 lg:px-4 lg:py-2 rounded-full text-xs font-semibold uppercase tracking-widest mb-4 lg:mb-5">
            <i className="ri-store-2-line"></i>
            Our Products
          </div>
          <h2 className="text-2xl sm:text-3xl lg:text-5xl font-extrabold text-white mb-3 lg:mb-4">
            Everything You Need in
            <span className="text-[#FF6600]"> One Place</span>
          </h2>
          <p className="text-white/50 text-sm lg:text-base max-w-xl">
            Browse our complete range of premium building materials, fixtures, and finishes.
          </p>
        </div>

        {/* Category Tabs */}
        <div className={`flex overflow-x-auto gap-2 lg:gap-3 mb-6 lg:mb-8 pb-1 scrollbar-hide transition-all duration-700 delay-200 ${headerAnim.isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'}`}>
          <button
            onClick={() => handleCategoryChange('all')}
            className={`px-3.5 py-2 lg:px-5 lg:py-2.5 rounded-full text-xs lg:text-sm font-semibold transition-all duration-200 whitespace-nowrap cursor-pointer flex-shrink-0 ${
              activeCategory === 'all'
                ? 'bg-[#FF6600] text-white shadow-lg shadow-[#FF6600]/30'
                : 'bg-white/5 text-white/60 hover:bg-white/10 hover:text-white border border-white/10'
            }`}
          >
            All Products
          </button>
          {categories.map((cat) => (
            <button
              key={cat.id}
              onClick={() => handleCategoryChange(cat.id)}
              className={`flex items-center gap-1.5 lg:gap-2 px-3.5 py-2 lg:px-5 lg:py-2.5 rounded-full text-xs lg:text-sm font-semibold transition-all duration-200 whitespace-nowrap cursor-pointer flex-shrink-0 ${
                activeCategory === cat.id
                  ? 'bg-[#FF6600] text-white shadow-lg shadow-[#FF6600]/30'
                  : 'bg-white/5 text-white/60 hover:bg-white/10 hover:text-white border border-white/10'
              }`}
            >
              <i className={`${cat.icon} text-sm lg:text-base`}></i>
              {cat.name}
            </button>
          ))}
        </div>

        {/* Subcategory Tabs */}
        {activeCategory !== 'all' && currentCategory && (
          <div className="flex overflow-x-auto gap-2 mb-7 lg:mb-10 pb-1 scrollbar-hide">
            <button
              onClick={() => setActiveSubcategory('all')}
              className={`px-3 py-1 lg:px-4 lg:py-1.5 rounded-full text-xs font-medium transition-all duration-200 whitespace-nowrap cursor-pointer flex-shrink-0 ${
                activeSubcategory === 'all'
                  ? 'bg-[#FF6600]/20 text-[#FF6600] border border-[#FF6600]/50'
                  : 'bg-transparent text-white/40 hover:text-white/70 border border-white/10'
              }`}
            >
              All
            </button>
            {currentCategory.items.map((item) => (
              <button
                key={item}
                onClick={() => setActiveSubcategory(item)}
                className={`px-3 py-1 lg:px-4 lg:py-1.5 rounded-full text-xs font-medium transition-all duration-200 whitespace-nowrap cursor-pointer flex-shrink-0 ${
                  activeSubcategory === item
                    ? 'bg-[#FF6600]/20 text-[#FF6600] border border-[#FF6600]/50'
                    : 'bg-transparent text-white/40 hover:text-white/70 border border-white/10'
                }`}
              >
                {item}
              </button>
            ))}
          </div>
        )}

        {/* Product Grid */}
        <div className="grid grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-2 lg:gap-4">
          {displayedProducts.map((product, index) => (
            <div
              key={product.id}
              onClick={() => setSelectedProduct(product)}
              className="group bg-[#1A1A1A] rounded-lg lg:rounded-xl overflow-hidden border border-white/5 hover:border-[#FF6600]/50 transition-all duration-300 hover:shadow-lg hover:shadow-[#FF6600]/10 hover:-translate-y-0.5 cursor-pointer animate-fadeInUp"
              style={{ animationDelay: `${index * 60}ms`, animationFillMode: 'both' }}
            >
              {/* Image */}
              <div className="relative aspect-[4/3] bg-[#141414] overflow-hidden">
                <img
                  src={product.image}
                  alt={product.name}
                  className="w-full h-full object-cover"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
                <div className="absolute top-1.5 left-1.5 lg:top-2 lg:left-2 bg-[#FF6600]/90 text-white text-[10px] font-semibold px-1.5 py-0.5 lg:px-2 lg:py-0.5 rounded-full">
                  {product.subcategory}
                </div>
              </div>

              {/* Info */}
              <div className="p-2.5 lg:p-4">
                <p className="text-white/40 text-[10px] uppercase tracking-widest mb-0.5">
                  {categories.find((c) => c.id === product.category)?.name}
                </p>
                <h3 className="text-white font-semibold text-xs lg:text-sm leading-snug mb-1.5 lg:mb-2 line-clamp-2 min-h-[2rem] lg:min-h-[2.5rem]">
                  {product.name}
                </h3>
                <div className="flex items-end justify-between">
                  <div>
                    <span className="text-[#FF6600]/80 text-[10px] font-semibold uppercase tracking-widest italic">Prices Coming Soon</span>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Show More / Show Less Button */}
        {hasMore && (
          <div className="flex justify-center mt-8 lg:mt-12">
            <button
              onClick={() => setShowAll((prev) => !prev)}
              className="group flex items-center gap-2.5 px-7 py-3 rounded-full border border-[#FF6600]/50 text-[#FF6600] text-sm font-semibold hover:bg-[#FF6600] hover:text-white transition-all duration-300 cursor-pointer whitespace-nowrap shadow-lg shadow-[#FF6600]/10 hover:shadow-[#FF6600]/30"
            >
              {showAll ? (
                <>
                  Show Less
                  <i className="ri-arrow-up-s-line text-base transition-transform duration-300"></i>
                </>
              ) : (
                <>
                  Show More
                  <span className="text-xs text-[#FF6600]/60 group-hover:text-white/70 transition-colors duration-300">
                    +{filtered.length - INITIAL_LIMIT} more
                  </span>
                  <i className="ri-arrow-down-s-line text-base transition-transform duration-300 group-hover:translate-y-0.5"></i>
                </>
              )}
            </button>
          </div>
        )}

        {/* Empty state */}
        {filtered.length === 0 && (
          <div className="text-center py-16 lg:py-24 text-white/30">
            <i className="ri-inbox-line text-4xl lg:text-5xl mb-4 block"></i>
            <p className="text-base lg:text-lg">No products found in this category.</p>
          </div>
        )}

        {/* Product Detail Modal */}
        <ProductModal
          product={selectedProduct}
          onClose={() => setSelectedProduct(null)}
        />
      </div>
    </section>
  );
}
